import React from 'react';
import {
    HelpCircle, Book, MessageCircle, Mail, ExternalLink,
    ChevronRight, Search,
} from 'lucide-react';
import PageShell from './PageShell';

const HelpPage = () => {
    const helpTopics = [
        {
            icon: Book,
            title: 'Getting Started',
            desc: 'Learn the basics of using Wadakaroo',
            articles: 5,
        },
        {
            icon: MessageCircle,
            title: 'Team Management',
            desc: 'Invite and manage your team members',
            articles: 8,
        },
        {
            icon: HelpCircle,
            title: 'Account & Billing',
            desc: 'Manage your subscription and payments',
            articles: 6,
        },
    ];

    const faqs = [
        {
            question: 'How do I invite team members?',
            answer: 'Go to Team Members page and click "Invite member" to send invitations via email.',
        },
        {
            question: 'Can I change my email address?',
            answer: 'Contact our support team to update your email address.',
        },
        {
            question: 'How do I upgrade my plan?',
            answer: 'Visit the Billing page to view available plans and upgrade your subscription.',
        },
    ];

    const [expandedFaq, setExpandedFaq] = React.useState(null);

    return (
        <PageShell
            title="Help & Support"
            description="Get help with Wadakaroo"
            icon={HelpCircle}
        >
            {/* Search */}
            <div className="relative max-w-xl mb-8">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                    type="text"
                    placeholder="Search help articles..."
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                />
            </div>

            {/* Help topics */}
            <h3 className="font-semibold text-gray-900 mb-4">Browse topics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {helpTopics.map((topic) => (
                    <button
                        key={topic.title}
                        className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:border-emerald-300 hover:shadow-sm transition-all group cursor-pointer"
                    >
                        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
                            <topic.icon size={20} className="text-emerald-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{topic.title}</h4>
                        <p className="text-sm text-gray-500 mb-2">{topic.desc}</p>
                        <p className="text-xs text-emerald-600 font-medium">
                            {topic.articles} articles
                        </p>
                    </button>
                ))}
            </div>

            {/* FAQs */}
            <h3 className="font-semibold text-gray-900 mb-4">Frequently asked questions</h3>
            <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100 mb-8">
                {faqs.map((faq, idx) => (
                    <div key={idx}>
                        <button
                            onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                            className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                        >
                            <span className="font-medium text-gray-900">{faq.question}</span>
                            <ChevronRight
                                size={18}
                                className={`text-gray-400 transition-transform ${
                                    expandedFaq === idx ? 'rotate-90' : ''
                                }`}
                            />
                        </button>
                        {expandedFaq === idx && (
                            <div className="px-5 pb-5 pt-0">
                                <p className="text-sm text-gray-600">{faq.answer}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Contact support */}
            <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                        <h3 className="text-xl font-display font-bold mb-2">
                            Still need help?
                        </h3>
                        <p className="text-emerald-100">
                            Our support team is here to assist you with any questions.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 rounded-xl font-semibold text-sm transition-colors">
                            <MessageCircle size={16} />
                            Live chat
                        </button>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl font-semibold text-sm transition-colors">
                            <Mail size={16} />
                            Email support
                        </button>
                    </div>
                </div>
            </div>
        </PageShell>
    );
};

export default HelpPage;